import { useState, useEffect, useRef, useCallback } from "react";
import { LoginPage } from "./LoginPage.tsx";
import { ClassList } from "./ClassList.tsx";
import { ClassDetail } from "./ClassDetail.tsx";
import { WeekView } from "./WeekView.tsx";
import { SearchPalette } from "./SearchPalette.tsx";
import { SidePanel, PanelStats } from "./SidePanel.tsx";
import { ToastProvider } from "./ToastContext.tsx";
import { todayStr, shiftDate, formatDateFi, localDateStr } from "./utils.ts";
import { CATEGORY_LABELS } from "./classCategory.ts";
import type { ClassCategory } from "./classCategory.ts";
import logoUrl from "./logo-header.png";

const HIDDEN_GYMS_KEY = "parkourakatemia:hiddenGyms";
const MODE_KEY = "parkourakatemia:mode";
const VIEW_MODE_KEY = "parkourakatemia:viewMode";

type Mode = "system" | "light" | "dark";
type ViewMode = "day" | "week";

function loadMode(): Mode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {}
  return "system";
}
function saveMode(mode: Mode) {
  if (mode === "system") {
    localStorage.removeItem(MODE_KEY);
  } else {
    localStorage.setItem(MODE_KEY, mode);
  }
}

function loadViewMode(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (v === "week") return "week";
  } catch {}
  return "day";
}

function loadHiddenGyms(): string[] {
  try { const raw = localStorage.getItem(HIDDEN_GYMS_KEY); if (raw) return JSON.parse(raw); } catch {}
  return [];
}
function saveHiddenGyms(gyms: string[]) { localStorage.setItem(HIDDEN_GYMS_KEY, JSON.stringify(gyms)); }

function getMondayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return localDateStr(d);
}

function formatWeekRange(dateStr: string): string {
  const monday = getMondayOfWeek(dateStr);
  const sunday = shiftDate(monday, 6);
  const mD = new Date(monday + "T12:00:00");
  const sD = new Date(sunday + "T12:00:00");
  const mDay = mD.getDate();
  const sDay = sD.getDate();
  const sMonth = sD.getMonth() + 1;
  if (mD.getMonth() === sD.getMonth()) {
    return `${mDay}.–${sDay}.${sMonth}.`;
  }
  return `${mDay}.${mD.getMonth() + 1}.–${sDay}.${sMonth}.`;
}

function useSwipe(handlers: { onSwipeLeft?: () => void; onSwipeRight?: () => void }) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    let startX = 0;
    let startY = 0;
    const onStart = (e: TouchEvent) => {
      startX = e.touches[0]!.clientX;
      startY = e.touches[0]!.clientY;
    };
    const onEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0]!.clientX - startX;
      const dy = e.changedTouches[0]!.clientY - startY;
      // Require 50px horizontal, and mostly horizontal (not vertical scroll)
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) handlersRef.current.onSwipeLeft?.();
        else handlersRef.current.onSwipeRight?.();
      }
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, []);
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

const IconSearch = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconStats = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="12" width="4" height="9" rx="1" /><rect x="10" y="7" width="4" height="14" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" />
  </svg>
);
const IconGear = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const IconLogout = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export function App() {
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [hiddenGyms, setHiddenGyms] = useState<string[]>(loadHiddenGyms);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [allGyms, setAllGyms] = useState<string[]>([]);
  const [date, setDate] = useState(todayStr);
  const [mode, setMode] = useState<Mode>(loadMode);
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  const [exiting, setExiting] = useState(false);
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const searchOpenRef = useRef(false);
  const isWideDay = useMediaQuery("(min-width: 640px)");
  const isWideWeek = useMediaQuery("(min-width: 1024px)");
  const isWide = viewMode === "week" ? isWideWeek : isWideDay;

  // Keep ref in sync with state
  searchOpenRef.current = searchOpen;

  useEffect(() => {
    fetch("/api/session")
      .then((r) => r.json())
      .then((d: { loggedIn: boolean }) => setLoggedIn(d.loggedIn))
      .catch(() => setLoggedIn(false));
  }, []);

  useEffect(() => {
    if (mode === "system") {
      document.documentElement.removeAttribute("data-mode");
    } else {
      document.documentElement.setAttribute("data-mode", mode);
    }
    saveMode(mode);
  }, [mode]);

  const toggleViewMode = (m: ViewMode) => {
    setViewMode(m);
    localStorage.setItem(VIEW_MODE_KEY, m);
  };

  const stepSize = viewMode === "week" ? 7 : 1;

  const goToDate = useCallback((newDate: string | ((prev: string) => string), dir: "left" | "right") => {
    setSlideDirection(dir);
    setDate(newDate);
  }, []);

  const toggleGym = (gym: string) => {
    setHiddenGyms((prev) => {
      const next = prev.includes(gym) ? prev.filter((g) => g !== gym) : [...prev, gym];
      saveHiddenGyms(next);
      return next;
    });
  };

  const handleBack = useCallback(() => {
    if (isWide) {
      setSelectedClassId(null);
      return;
    }
    setExiting(true);
    setTimeout(() => {
      setSelectedClassId(null);
      setExiting(false);
    }, 250);
  }, [isWide]);

  const handleSwipeLeft = useCallback(() => {
    if (!selectedClassId) {
      goToDate((d) => shiftDate(d, stepSize), "right");
    }
  }, [selectedClassId, stepSize, goToDate]);

  const handleSwipeRight = useCallback(() => {
    if (selectedClassId) {
      handleBack();
    } else {
      goToDate((d) => shiftDate(d, -stepSize), "left");
    }
  }, [selectedClassId, stepSize, handleBack, goToDate]);

  useSwipe({ onSwipeLeft: handleSwipeLeft, onSwipeRight: handleSwipeRight });

  // Keyboard navigation
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Skip when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      // Skip when search palette is open (it handles its own keys)
      if (searchOpenRef.current) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setDate((d) => shiftDate(d, -stepSize));
        setSlideDirection("left");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setDate((d) => shiftDate(d, stepSize));
        setSlideDirection("right");
      } else if (e.key === "Escape" && selectedClassId) {
        e.preventDefault();
        handleBack();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [stepSize, selectedClassId, handleBack]);

  // Cmd/Ctrl+K for search (separate listener so it works even in inputs)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleSearchSelect = useCallback((classId: string, classDate: string) => {
    setSearchOpen(false);
    if (classDate !== date) {
      setDate(classDate);
    }
    setSelectedClassId(classId);
  }, [date]);

  if (loggedIn === null) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  if (!loggedIn) {
    return <LoginPage onLogin={() => setLoggedIn(true)} />;
  }

  const handleLogout = async () => {
    await fetch("/api/logout", { method: "POST" });
    setLoggedIn(false);
    setSelectedClassId(null);
  };

  const isToday = date === todayStr();

  const dateNav = (
    <div className="date-nav">
      <button className="date-nav-btn" onClick={() => goToDate((d) => shiftDate(d, -stepSize), "left")}>
        ←
      </button>
      <button
        className="date-nav-current"
        onClick={() => dateInputRef.current?.showPicker()}
      >
        {viewMode === "week" ? formatWeekRange(date) : formatDateFi(date)}
      </button>
      <input
        ref={dateInputRef}
        type="date"
        className="date-picker-hidden"
        value={date}
        onChange={(e) => {
          const v = (e.target as HTMLInputElement).value;
          if (v) goToDate(v, v > date ? "right" : "left");
        }}
      />
      <button
        className={`date-nav-today ${isToday ? "is-today" : ""}`}
        onClick={() => { const t = todayStr(); goToDate(t, t > date ? "right" : "left"); }}
        disabled={isToday}
      >
        Tänään
      </button>
      <button className="date-nav-btn" onClick={() => goToDate((d) => shiftDate(d, stepSize), "right")}>
        →
      </button>
      <div className="view-toggle">
        <button
          className={`view-toggle-btn${viewMode === "day" ? " active" : ""}`}
          onClick={() => toggleViewMode("day")}
        >
          Pv
        </button>
        <button
          className={`view-toggle-btn${viewMode === "week" ? " active" : ""}`}
          onClick={() => toggleViewMode("week")}
        >
          Vk
        </button>
      </div>
    </div>
  );

  const listView = (
    <div
      key={viewMode === "week" ? `w-${getMondayOfWeek(date)}` : `d-${date}`}
      className={`slide-container${slideDirection === "right" ? " slide-from-right" : slideDirection === "left" ? " slide-from-left" : ""}`}
      onAnimationEnd={() => setSlideDirection(null)}
    >
      {viewMode === "week" ? (
        <WeekView
          date={date}
          onSelect={(id) => setSelectedClassId(id)}
          onDaySelect={(d) => { setDate(d); toggleViewMode("day"); }}
          onSessionExpired={() => setLoggedIn(false)}
          hiddenGyms={hiddenGyms}
          selectedClassId={isWide ? selectedClassId : undefined}
        />
      ) : (
        <ClassList
          date={date}
          onSelect={(id) => setSelectedClassId(id)}
          onSessionExpired={() => setLoggedIn(false)}
          hiddenGyms={hiddenGyms}
          onGymsLoaded={setAllGyms}
          selectedClassId={isWide ? selectedClassId : undefined}
        />
      )}
    </div>
  );

  return (
    <ToastProvider>
    <div>
      <div className={`header${selectedClassId && !isWide ? " header-detail" : ""}`}>
        <img src={logoUrl} alt="Parkourakatemia" className="header-logo" />
        <div className="header-buttons">
          <button className="header-icon-btn" onClick={() => setSearchOpen(true)} aria-label="Haku">
            <IconSearch />
          </button>
          {!isWide && (
            <button className="header-icon-btn" onClick={() => setStatsOpen(true)} aria-label="Tilastot">
              <IconStats />
            </button>
          )}
          <button className="header-icon-btn" onClick={() => setSettingsOpen((o) => !o)} aria-label="Asetukset">
            <IconGear />
          </button>
          <button className="header-icon-btn" onClick={handleLogout} aria-label="Kirjaudu ulos">
            <IconLogout />
          </button>
        </div>
      </div>
      {settingsOpen && (
        <div className="settings-panel">
          <h3>Ulkoasu</h3>
          <div className="mode-toggle">
            {([["system", "Järjestelmä"], ["light", "Vaalea"], ["dark", "Tumma"]] as const).map(([id, label]) => (
              <button
                key={id}
                className={`mode-btn${mode === id ? " active" : ""}`}
                onClick={() => setMode(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <h3>Näytettävät salit</h3>
          {allGyms.length === 0 && <div className="settings-hint">Ladataan...</div>}
          {allGyms.map((gym) => (
            <label key={gym} className="gym-toggle">
              <input
                type="checkbox"
                checked={!hiddenGyms.includes(gym)}
                onChange={() => toggleGym(gym)}
              />
              {gym}
            </label>
          ))}
          <h3>Tuntivärit</h3>
          <div className="category-legend">
            {(Object.keys(CATEGORY_LABELS) as ClassCategory[]).map((cat) => (
              <span key={cat} className="category-legend-item">
                <span className="category-dot" style={{ backgroundColor: `var(--cat-${cat})` }} />
                {CATEGORY_LABELS[cat]}
              </span>
            ))}
          </div>
        </div>
      )}
      {(!selectedClassId || isWide) && dateNav}
      {isWide ? (
        <div className="wide-layout">
          <div className="wide-list-panel">
            {listView}
          </div>
          <SidePanel
            selectedClassId={selectedClassId}
            onDismiss={() => setSelectedClassId(null)}
            onSessionExpired={() => { setLoggedIn(false); setSelectedClassId(null); }}
            date={date}
            viewMode={viewMode}
          />
        </div>
      ) : (
        selectedClassId ? (
          <ClassDetail
            classId={selectedClassId}
            onBack={handleBack}
            onSessionExpired={() => { setLoggedIn(false); setSelectedClassId(null); }}
            exiting={exiting}
          />
        ) : listView
      )}
      {searchOpen && (
        <SearchPalette
          onSelect={handleSearchSelect}
          onClose={() => setSearchOpen(false)}
        />
      )}
      {statsOpen && !isWide && (
        <div className="search-backdrop" onClick={() => setStatsOpen(false)}>
          <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
            <PanelStats date={date} viewMode={viewMode} />
          </div>
        </div>
      )}
    </div>
    </ToastProvider>
  );
}
